import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Filter, X } from 'lucide-react';
import { UI_Z_INDEX } from '../utils/uiZIndex';

export type MultiSelectOption = {
  value: string;
  label: string;
  left?: React.ReactNode;
  right?: string;
  /** Not available for the planned slot: row is dimmed, badge on the right, detail as tooltip. */
  unavailable?: { label: string; detail: string };
};

type AppMultiSelectDropdownProps = {
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  emptyLabel?: string;
  /** Leer-Text im Trigger (form-Variante); Panel-Titel bleibt `label`. */
  placeholder?: string;
  width?: number;
  className?: string;
  /** Filter-Button wie in der Plantafel-Toolbar (Trichter-Icon, weißer Trigger). */
  variant?: 'default' | 'filter' | 'form';
  /** z-index für portaliertes Panel (z. B. in Modals). */
  menuZIndex?: number;
};

export function AppMultiSelectDropdown({
  label,
  selected,
  onChange,
  options,
  emptyLabel = 'Keine Einträge',
  placeholder,
  width = 260,
  className = '',
  variant = 'default',
  menuZIndex = UI_Z_INDEX.portalDropdown,
}: AppMultiSelectDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (val: string) => {
    if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
    else onChange([...selected, val]);
  };

  const active = selected.length > 0;
  const isFormVariant = variant === 'form';
  const selectedLabels = React.useMemo(() => {
    const byValue = new Map(options.map((o) => [o.value, o.label]));
    return selected.map((v) => byValue.get(v)).filter((name): name is string => Boolean(name?.trim()));
  }, [options, selected]);
  const emptyTriggerText = placeholder ?? label;
  const summary = React.useMemo(() => {
    if (!active) return emptyTriggerText;
    if (isFormVariant) {
      return selectedLabels.join(', ') || emptyTriggerText;
    }
    return `${label} · ${selected.length}`;
  }, [active, emptyTriggerText, isFormVariant, label, selected.length, selectedLabels]);

  const panel = open ? (
    <div
      ref={panelRef}
      className="fixed flex flex-col overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-lg"
      style={{
        top: triggerRef.current ? triggerRef.current.getBoundingClientRect().bottom + (isFormVariant ? 4 : 6) : 0,
        ...(isFormVariant && triggerRef.current
          ? {
              left: triggerRef.current.getBoundingClientRect().left,
              width: triggerRef.current.getBoundingClientRect().width,
              minWidth: triggerRef.current.getBoundingClientRect().width,
            }
          : {
              right: triggerRef.current
                ? window.innerWidth - triggerRef.current.getBoundingClientRect().right
                : 0,
              width,
              minWidth: width,
            }),
        maxWidth: 'min(420px, calc(100vw - 24px))',
        maxHeight: 360,
        zIndex: menuZIndex,
      }}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-app-border bg-app-surface px-2 py-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-app-text-subtle">{label}</span>
        {active && (
          <button
            type="button"
            className="text-[11px] font-medium text-blue-600 hover:underline"
            onClick={() => onChange([])}
          >
            Zurücksetzen
          </button>
        )}
      </div>
      <div className="app-scrollbar-subtle min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-1">
      {options.length === 0 ? (
        <div className="px-2 py-2 text-xs text-gray-400">{emptyLabel}</div>
      ) : (
        options.map((o) => {
          const checked = selected.includes(o.value);
          return (
            <label
              key={o.value}
              className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-app-text hover:bg-app-hover ${
                checked ? 'bg-app-surface-muted' : ''
              }`}
              title={o.unavailable?.detail}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(o.value)}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-green-600"
              />
              {o.left && (
                <span className={`mt-0.5 inline-flex shrink-0 ${o.unavailable ? 'opacity-60' : ''}`}>{o.left}</span>
              )}
              {o.right ? (
                <span className={`min-w-0 flex-1 ${o.unavailable ? 'opacity-60' : ''}`}>
                  <span
                    className="block text-[12.5px] font-medium leading-snug text-app-text break-words"
                    title={o.label}
                  >
                    {o.label}
                  </span>
                  <span className="block text-[11px] leading-snug text-app-text-subtle break-words" title={o.right}>
                    {o.right}
                  </span>
                </span>
              ) : (
                <span
                  className={`min-w-0 flex-1 text-[12.5px] leading-snug break-words ${o.unavailable ? 'opacity-60' : ''}`}
                  title={o.unavailable ? undefined : o.label}
                >
                  {o.label}
                </span>
              )}
              {o.unavailable ? (
                <span className="mt-0.5 shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-medium leading-none text-amber-800">
                  {o.unavailable.label}
                </span>
              ) : null}
            </label>
          );
        })
      )}
      </div>
    </div>
  ) : null;

  return (
    <div ref={triggerRef} className={`relative ${isFormVariant ? 'flex w-full min-w-0' : 'inline-flex'} ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={
          isFormVariant
            ? `flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-app-border bg-app-surface px-2.5 text-left text-sm outline-none transition-colors hover:border-app-border-strong focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue ${
                active ? 'text-app-text' : 'text-app-text-subtle'
              }`
            : `inline-flex items-center gap-1.5 rounded-md border text-[11.5px] font-medium leading-none ${
                variant === 'filter'
                  ? `px-2.5 py-1 ${
                      active
                        ? 'border-app-border-strong bg-app-surface-muted text-app-text'
                        : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
                    }`
                  : `h-8 px-2.5 ${
                      active
                        ? 'border-blue-200 bg-blue-50 text-blue-800'
                        : 'border-app-border bg-app-surface text-app-text-muted'
                    }`
              }`
        }
      >
        {variant === 'filter' && <Filter className="h-3.5 w-3.5 shrink-0 text-app-text-subtle" aria-hidden />}
        <span className="min-w-0 truncate" title={active && isFormVariant ? summary : undefined}>
          {summary}
        </span>
        {active ? (
          <span
            role="button"
            tabIndex={0}
            className={`inline-flex shrink-0 items-center justify-center rounded-full ${
              isFormVariant ? 'h-4 w-4 bg-app-surface-muted text-app-text-muted' : 'h-3.5 w-3.5 bg-blue-100 text-blue-800'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onChange([]);
              }
            }}
          >
            <X className={isFormVariant ? 'h-2.5 w-2.5' : 'h-2 w-2'} strokeWidth={3} />
          </span>
        ) : (
          <ChevronDown
            className={`shrink-0 text-app-text-subtle transition-transform ${open ? 'rotate-180' : ''} ${
              isFormVariant ? 'h-4 w-4' : 'h-2.5 w-2.5 text-gray-400'
            }`}
          />
        )}
      </button>
      {open && typeof document !== 'undefined' && createPortal(panel, document.body)}
    </div>
  );
}
